/* import __COLOCATED_TEMPLATE__ from './early-stage-extension-gift-banner.hbs'; */
/* RESPONSIBLE TEAM: team-frontend-tech */

import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import moment from 'moment-timezone';
import type BannerDismissalService from 'embercom/services/banner-dismissal-service';
import { restartableTask } from 'ember-concurrency-decorators';
import { tracked } from '@glimmer/tracking';
import { taskFor } from 'ember-concurrency-ts';

interface Args {}

export default class EarlyStageExtensionGiftBanner extends Component<Args> {
  @service declare appService: AppService;
  @service declare customerService: $TSFixMe;
  @service declare cardlessTrialService: $TSFixMe;
  @service declare permissionsService: $TSFixMe;
  @service declare bannerDismissalService: BannerDismissalService;
  @service declare intercomEventService: $TSFixMe;
  @tracked adminDismissedBanner = false;
  @tracked localEarlyStageGraduation: $TSFixMe = null;
  dismissalKey = 'early-stage-extension-gift-banner';

  constructor(owner: unknown, args: any) {
    super(owner, args);
    taskFor(this.loadDataAndPoll).perform();
  }

  get shouldShowBanner() {
    let adminHasBillingPermission = this.permissionsService.currentAdminCan(
      'can_access_billing_settings',
    );

    return (
      !this.adminDismissedBanner &&
      adminHasBillingPermission &&
      this.app?.shouldSeeUsageBasedGiftBanner &&
      !!this.localEarlyStageGraduation
    );
  }

  get app() {
    return this.appService.app;
  }

  get earlyStagePartner() {
    return this.app?.earlyStagePartner;
  }

  get currentStepPercentageDiscount() {
    return this.earlyStageGraduation?.currentStepPercentageDiscount;
  }

  get year2CouponPercentOff() {
    return this.earlyStageGraduation?.year2CouponPercentOff;
  }

  get nextStepPercentageDiscount() {
    return this.earlyStageGraduation?.nextStepPercentageDiscount;
  }

  get earlyStageGraduation() {
    return this.localEarlyStageGraduation;
  }

  get formattedGraduationDate() {
    let graduationDate = moment.utc(this.app?.earlyStageGraduationDate);
    return graduationDate.format('MMMM D, YYYY');
  }

  @restartableTask
  *loadDataAndPoll() {
    // Load dismissal status first
    this.adminDismissedBanner = yield this.bannerDismissalService.hasDismissed(this.dismissalKey);

    // Ensure customer data is loaded
    yield this.customerService.ensureDataIsLoaded.perform();
    // this.cardlessTrialService.initializeUsingApp(this.app);

    // Load early stage data
    yield this.customerService.loadEarlyStageState();

    // Sync to local tracked property
    this.localEarlyStageGraduation = this.customerService.earlyStageGraduation;

    // Track analytics if banner should be shown now
    if (this.shouldShowBanner) {
      this.intercomEventService.trackAnalyticsEvent({
        action: 'viewed',
        object: 'usage_based_gift_banner',
        context: this.dismissalKey,
      });
    }
  }

  @restartableTask
  *handleDismissClick() {
    this.intercomEventService.trackAnalyticsEvent({
      action: 'dismissed',
      object: 'usage_based_gift_banner',
      context: this.dismissalKey,
    });
    yield this.bannerDismissalService.dismiss(this.dismissalKey);
    this.adminDismissedBanner = true;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Banners::EarlyStageExtensionGiftBanner': typeof EarlyStageExtensionGiftBanner;
    'banners/early-stage-extension-gift-banner': typeof EarlyStageExtensionGiftBanner;
  }
}
